import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send, X, Trash2, Loader2, Maximize2, Minimize2, Globe, Building2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ChatMessage } from '../../ai-assistant/types';

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  messages: ChatMessage[];
  error: string | null;
  onSendMessage: (content: string) => void;
  onToggle: () => void;
  onClear: () => void;
  globalMode: boolean;
  onToggleGlobalMode: () => void;
  loadingGlobal: boolean;
  vpcCount: number;
}

const SuggestedQuery: React.FC<{ children: string; onClick: (q: string) => void }> = ({
  children,
  onClick,
}) => (
  <button
    onClick={() => onClick(children)}
    className="block w-full text-left px-2 py-1.5 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded transition-colors dark:text-semantic-text-brand dark:bg-semantic-bg-brand-secondary dark:hover:bg-semantic-hover-brand"
  >
    {children}
  </button>
);

const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[90%] ${
          isUser ? 'bg-semantic-bg-brand-bold text-semantic-fg-base' : 'bg-semantic-bg-secondary border border-semantic-border-primary'
        } rounded-xl px-3 py-2 shadow-sm`}
      >
        {isUser ? (
          <p className="text-xs">{message.content}</p>
        ) : (
          <div className="ai-response text-[11px] leading-relaxed text-semantic-text-primary">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Headings - use semantic tokens for dark mode visibility
                h1: ({ children }) => <h1 className="text-sm font-bold text-semantic-text-primary mt-2 mb-1">{children}</h1>,
                h2: ({ children }) => <h2 className="text-xs font-bold text-semantic-text-primary mt-2 mb-1">{children}</h2>,
                h3: ({ children }) => <h3 className="text-xs font-semibold text-semantic-text-primary mt-1.5 mb-0.5">{children}</h3>,
                // Paragraphs
                p: ({ children }) => <p className="my-1 text-semantic-text-primary">{children}</p>,
                // Lists
                ul: ({ children }) => <ul className="my-1 ml-3 space-y-0.5 list-disc text-semantic-text-primary">{children}</ul>,
                ol: ({ children }) => <ol className="my-1 ml-3 space-y-0.5 list-decimal text-semantic-text-primary">{children}</ol>,
                li: ({ children }) => <li className="text-[11px] text-semantic-text-primary">{children}</li>,
                // Bold/Strong
                strong: ({ children }) => <strong className="font-semibold text-semantic-text-primary">{children}</strong>,
                // Code - semantic bg/border/text for dark mode
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
                code({ node, inline, className, children, ...props }: any) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <div className="my-1.5">
                      <SyntaxHighlighter
                        style={oneDark}
                        language={match[1]}
                        PreTag="div"
                        customStyle={{ fontSize: '10px', padding: '8px', borderRadius: '6px', margin: 0 }}
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    </div>
                  ) : (
                    <code className="bg-semantic-bg-tertiary text-semantic-text-primary border border-semantic-border-primary px-1 py-0.5 rounded text-[10px] font-mono" {...props}>
                      {children}
                    </code>
                  );
                },
                // Blockquote
                blockquote: ({ children }) => (
                  <blockquote className="border-l-2 border-semantic-border-brand-primary pl-2 my-1 text-semantic-text-secondary italic">
                    {children}
                  </blockquote>
                ),
                // Horizontal rule
                hr: () => <hr className="my-2 border-semantic-border-primary" />,
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}
        <div className={`text-[9px] mt-1 ${isUser ? 'text-[var(--color-semantic-text-inverse-secondary)]' : 'text-semantic-text-secondary'}`}>
          {message.timestamp.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export const AIAssistantPanel: React.FC<Props> = ({
  isOpen,
  isLoading,
  messages,
  error,
  onSendMessage,
  onToggle,
  onClear,
  globalMode,
  onToggleGlobalMode,
  loadingGlobal,
  vpcCount,
}) => {
  const [input, setInput] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      onSendMessage(input.trim());
      setInput('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Floating button when closed
  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:scale-105"
        title="Open AI Assistant"
      >
        <Bot className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
          <span className="text-[8px] text-white font-bold">AI</span>
        </span>
      </button>
    );
  }

  const panelWidth = isExpanded ? 'w-[550px]' : 'w-[380px]';
  const panelHeight = isExpanded ? 'h-[75vh]' : 'h-[500px]';

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 ${panelWidth} ${panelHeight} bg-semantic-bg-secondary rounded-xl shadow-2xl border border-semantic-border-primary flex flex-col overflow-hidden transition-all duration-300`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
            <Bot className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-semibold text-xs">Network Intelligence</h3>
            <p className="text-[9px] text-white/70">
              {loadingGlobal ? 'Loading...' : `${vpcCount} VPCs`}
            </p>
          </div>
        </div>
        {/* Global Mode Toggle */}
        <button
          onClick={onToggleGlobalMode}
          disabled={loadingGlobal}
          className={`flex items-center gap-1 px-2 py-1 rounded text-[9px] font-medium transition-colors ${
            globalMode
              ? 'bg-amber-400 text-amber-900'
              : 'bg-white/20 text-white hover:bg-white/30'
          } ${loadingGlobal ? 'opacity-50 cursor-wait' : ''}`}
          title={globalMode ? 'Global: All accounts' : 'Current account only'}
        >
          {globalMode ? <Globe className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
          {globalMode ? 'Global' : 'Current'}
        </button>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            title={isExpanded ? 'Minimize' : 'Expand'}
          >
            {isExpanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={onClear}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            title="Clear chat"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onToggle}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            title="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-semantic-bg-primary">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto mb-3 bg-indigo-100 dark:bg-semantic-bg-brand-secondary rounded-xl flex items-center justify-center">
              <Bot className="w-6 h-6 text-indigo-500 dark:text-semantic-text-brand" />
            </div>
            <h4 className="font-semibold text-semantic-text-primary text-sm mb-1">Network Intelligence</h4>
            <p className="text-xs text-semantic-text-secondary mb-3">
              Ask about topology, security, or connectivity.
            </p>
            <div className="space-y-1.5">
              <SuggestedQuery onClick={onSendMessage}>
                What security rules need remediation?
              </SuggestedQuery>
              <SuggestedQuery onClick={onSendMessage}>
                Which resources are exposed to internet?
              </SuggestedQuery>
              <SuggestedQuery onClick={onSendMessage}>
                Analyze path from 10.0.1.0/24 to 10.0.5.0/24
              </SuggestedQuery>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}

        {isLoading && (
          <div className="flex items-center gap-2 text-semantic-text-secondary">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span className="text-xs">Analyzing...</span>
          </div>
        )}

        {error && (
          <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs dark:bg-semantic-bg-danger-secondary dark:border-semantic-border-danger-primary dark:text-semantic-text-danger">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-2 border-t border-semantic-border-primary bg-semantic-bg-secondary">
        <div className="flex items-end gap-1.5">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your network..."
            rows={1}
            className="flex-1 px-2.5 py-1.5 border border-semantic-border-primary rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-semantic-outline-primary focus:border-transparent text-xs bg-semantic-bg-secondary text-semantic-text-primary placeholder:text-semantic-text-secondary"
            style={{ maxHeight: '80px' }}
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="p-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default AIAssistantPanel;
